import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const presets = [
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_4_weeks', label: 'Last 4 weeks' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'week_to_date', label: 'Week to date' },
  { value: 'month_to_date', label: 'Month to date' },
  { value: 'year_to_date', label: 'Year to date' },
  { value: 'all_time', label: 'All time' }
];

const DateRangeSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customRange, setCustomRange] = useState({
    start: value?.start ? new Date(value.start) : new Date(),
    end: value?.end ? new Date(value.end) : new Date()
  });
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCustom(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetSelect = (preset) => {
    onChange({ preset, start: null, end: null });
    setIsOpen(false);
    setShowCustom(false);
  };

  const handleCustomApply = () => {
    onChange({
      preset: 'custom',
      start: customRange.start,
      end: customRange.end
    });
    setIsOpen(false);
    setShowCustom(false);
  };

  const getDisplayLabel = () => {
    if (value?.preset === 'custom' && value?.start && value?.end) {
      const startStr = new Date(value.start).toLocaleDateString();
      const endStr = new Date(value.end).toLocaleDateString();
      return `${startStr} - ${endStr}`;
    }
    const preset = presets.find(p => p.value === value?.preset);
    return preset?.label || 'Select date range';
  };

  return (
    <div className="date-range-selector" ref={dropdownRef}>
      <button
        className="date-range-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
        <span>{getDisplayLabel()}</span>
        <svg className={`chevron ${isOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div className="date-range-dropdown">
          {!showCustom ? (
            <>
              <div className="preset-list">
                {presets.map(preset => (
                  <button
                    key={preset.value}
                    className={`preset-item ${value?.preset === preset.value ? 'active' : ''}`}
                    onClick={() => handlePresetSelect(preset.value)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="dropdown-divider"></div>
              <button
                className="custom-range-btn"
                onClick={() => setShowCustom(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Custom range
              </button>
            </>
          ) : (
            <div className="custom-range-panel">
              <div className="custom-range-header">
                <button className="back-btn" onClick={() => setShowCustom(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <span>Custom date range</span>
              </div>
              <div className="dual-calendar">
                <DatePicker
                  selected={customRange.start}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setCustomRange({ start, end: end || start });
                  }}
                  startDate={customRange.start}
                  endDate={customRange.end}
                  selectsRange
                  inline
                  monthsShown={2}
                />
              </div>
              <div className="custom-range-footer">
                <div className="range-display">
                  <span>Range: </span>
                  <span className="range-dates">
                    {customRange.start?.toLocaleDateString()} - {customRange.end?.toLocaleDateString()}
                  </span>
                </div>
                <div className="range-actions">
                  <button className="btn-style-border" onClick={() => setShowCustom(false)}>
                    Cancel
                  </button>
                  <button className="btn-style-new" onClick={handleCustomApply}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;
